import { type AxiosError } from "axios";

import UnknownError from "./UnknownError";

export const isStandardError = (unknownError: unknown): unknownError is Error => unknownError instanceof Error;

export const isAxiosError = <T extends object>(unknownError: unknown): unknownError is AxiosError<T> =>
  Boolean(unknownError && typeof unknownError === "object" && "isAxiosError" in unknownError);

// `.unwrap()` na odrzuconym thunku RTK rzuca zserializowany błąd (`{ name, message, stack }`),
// a nie `Error` - bez tego każdy komunikat z thunka kończył jako „UnknownError".
const getSerializedMessage = (unknownError: unknown) => {
  if (!unknownError || typeof unknownError !== "object" || !("message" in unknownError)) {
    return null;
  }
  return typeof unknownError.message === "string" && unknownError.message ? unknownError.message : null;
};

export const getErrorMessage = (unknownError: unknown): string =>
  isStandardError(unknownError) ? unknownError.message : (getSerializedMessage(unknownError) ?? UnknownError.message);

const safeStringify = (value: unknown): string => {
  try {
    const seen = new WeakSet();
    return JSON.stringify(value, (_key, v) => {
      if (typeof v === "object" && v !== null) {
        if (seen.has(v as object)) return "[Circular]";
        seen.add(v as object);
      }
      if (v instanceof Error) {
        return { name: v.name, message: v.message, stack: v.stack };
      }
      return v;
    });
  } catch {
    return "";
  }
};

export const inferAsError = (unknownError: unknown): Error => {
  if (unknownError instanceof Error) return unknownError;

  if (unknownError && typeof unknownError === "object") {
    const maybeMessage = (unknownError as { message?: unknown }).message;
    const message =
      typeof maybeMessage === "string" && maybeMessage ? maybeMessage : safeStringify(unknownError) || "Unknown error";
    const err = new Error(message);
    try {
      Object.defineProperty(err, "cause", { value: unknownError, enumerable: false });
    } catch {
      /* noop */
    }
    return err;
  }

  return new Error(unknownError === undefined ? "Unknown error" : String(unknownError));
};

export const getError = (
  error: unknown,
): Error & {
  status: number;
  code?: string | number;
  errorCode?: string | number;
  statusText?: string;
  response?: { message?: string; data?: object; status?: string | number };
} => {
  if (isAxiosError(error)) {
    return {
      status: error.response?.status || 500,
      message:
        error.response?.data && "message" in error.response.data
          ? (error.response.data.message as string)
          : "Unknown error",
      name: "AxiosError",
    };
  }
  return isStandardError(error) ? (error as Error & { status: number }) : new UnknownError(error);
};
